/**
 * Generic Admin Resource Manager
 * Handles list + create + delete for a resource with a customizable field schema.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";
import { ADMIN } from "@/constants/testIds";

export default function AdminResource({ title, subtitle, endpoint, adminEndpoint, fields, columns, renderRow, keyName = "id" }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get(endpoint).then((r) => setItems(r.data));

  useEffect(() => {
    load();
    const init = {};
    fields.forEach((f) => (init[f.name] = f.default ?? ""));
    setForm(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      fields.forEach((f) => {
        if (f.type === "number") payload[f.name] = Number(payload[f.name]) || 0;
      });
      await api.post(adminEndpoint, payload);
      toast.success(`${title} berhasil ditambahkan`);
      setOpen(false);
      const init = {};
      fields.forEach((f) => (init[f.name] = f.default ?? ""));
      setForm(init);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Yakin hapus item ini?")) return;
    try {
      await api.delete(`${adminEndpoint}/${id}`);
      toast.success("Berhasil dihapus");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#0086C6] font-semibold">CMS</div>
          <h1 className="font-heading font-bold text-3xl text-[#003A5C]">{title}</h1>
          {subtitle && <p className="text-slate-600 text-sm mt-1">{subtitle}</p>}
        </div>
        <button onClick={() => setOpen(true)} data-testid={ADMIN.addNew(title.toLowerCase().replace(/\s/g, "-"))}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#005F8E] hover:bg-[#003A5C] text-white font-semibold text-sm transition-colors">
          <Plus className="w-4 h-4" /> Tambah {title}
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {columns.map((c) => <th key={c} className="text-left px-5 py-3 text-xs uppercase tracking-widest font-semibold text-slate-600">{c}</th>)}
              <th className="px-5 py-3 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={columns.length + 1} className="text-center py-10 text-slate-400">Belum ada data</td></tr>
            )}
            {items.map((it) => (
              <tr key={it[keyName]} className="border-b border-slate-100 hover:bg-slate-50">
                {renderRow(it).map((cell, i) => <td key={i} className="px-5 py-3 text-[#003A5C]">{cell}</td>)}
                <td className="px-5 py-3">
                  <button onClick={() => remove(it[keyName])} data-testid={ADMIN.deleteBtn(title.toLowerCase().replace(/\s/g, "-"), it[keyName])}
                    className="p-2 rounded-lg text-red-500 hover:bg-red-50" aria-label="Hapus">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-[#003A5C]/70 backdrop-blur-sm grid place-items-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100 sticky top-0 bg-white">
              <h2 className="font-heading font-bold text-xl text-[#003A5C]">Tambah {title}</h2>
              <button type="button" onClick={() => setOpen(false)} className="p-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              {fields.map((f) => (
                <div key={f.name}>
                  <label className="block text-xs uppercase tracking-widest text-slate-600 font-semibold mb-1.5">{f.label}{f.required && " *"}</label>
                  {f.type === "textarea" ? (
                    <textarea required={f.required} rows={5} value={form[f.name] || ""} onChange={set(f.name)}
                      className="w-full rounded-lg border border-slate-200 px-4 py-2.5 focus:border-[#00B4D8] outline-none" />
                  ) : (
                    <input type={f.type || "text"} required={f.required} value={form[f.name] ?? ""} onChange={set(f.name)}
                      className="w-full rounded-lg border border-slate-200 px-4 py-2.5 focus:border-[#00B4D8] outline-none" placeholder={f.placeholder} />
                  )}
                </div>
              ))}
            </div>
            <div className="p-6 border-t border-slate-100 flex justify-end gap-3">
              <button type="button" onClick={() => setOpen(false)} className="px-5 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-semibold">Batal</button>
              <button type="submit" disabled={busy} className="px-5 py-2.5 rounded-lg bg-[#005F8E] hover:bg-[#003A5C] text-white text-sm font-semibold disabled:opacity-60">
                {busy ? "Menyimpan..." : "Simpan"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
